(() => {
'use strict';

  const NS = 'SenixVisualYardFinder';
  const grassNames = {
    'bermuda': 'Bermuda Grass',
    'tall-fescue': 'Tall Fescue',
    'kentucky-bluegrass': 'Kentucky Bluegrass',
    'st-augustine': 'St. Augustine Grass'
  };
  const grassDetails = {
    'bermuda': 'Fine, dense warm-season turf that spreads along the ground.',
    'tall-fescue': 'Coarse, upright cool-season blades that grow in bunches.',
    'kentucky-bluegrass': 'Soft, dense cool-season turf with a carpet-like habit.',
    'st-augustine': 'Broad, coarse warm-season blades with spreading runners.'
  };

  function init(root) {
    if (!root || root.__syfController) return;
    const controller = new AbortController();
    const signal = controller.signal;
    root.__syfController = controller;

    const discovery = root.querySelector('[data-syf-discovery]');
    const result = root.querySelector('[data-syf-result]');
    const resultContent = root.querySelector('[data-syf-result-content]');
    let stateMap = {};
    try { stateMap = JSON.parse(root.querySelector('[data-syf-state-map]')?.textContent || '{}'); } catch (_) {}
    const focusHeading = () => requestAnimationFrame(() => resultContent.querySelector('[data-syf-result-heading]')?.focus({ preventScroll: true }));
    const scrollToRoot = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      const headerHeight = Array.from(document.querySelectorAll('.shopify-section-header-sticky')).reduce((height, header) => {
        const style = window.getComputedStyle(header);
        const rect = header.getBoundingClientRect();
        const active = style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0;
        return active ? Math.max(height, rect.height) : height;
      }, 0);
      const top = Math.max(0, root.getBoundingClientRect().top + window.scrollY - headerHeight - 12);
      window.scrollTo({
        top,
        left: window.scrollX,
        behavior: 'auto'
      });
      window.setTimeout(() => {
        const retryTop = Math.max(0, root.getBoundingClientRect().top + window.scrollY - headerHeight - 12);
        window.scrollTo({ top: retryTop, left: window.scrollX, behavior: 'auto' });
      }, 250);
    }));

    function setToolbar(mode) {
      root.querySelectorAll('.syf-vyf__toolbar [data-syf-view]').forEach((button) => {
        const active = button.dataset.syfView === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'page' : 'false');
      });
    }

    function showDiscovery(mode = 'all') {
      result.hidden = true;
      discovery.hidden = false;
      resultContent.replaceChildren();
      if (mode === 'yard' || mode === 'lawn') discovery.dataset.focusedMode = mode;
      else discovery.removeAttribute('data-focused-mode');
      root.dataset.syfState = mode === 'all' ? 'discovery' : `${mode}-selection`;
      const target = mode === 'all' ? root.querySelector('[data-syf-option]') : root.querySelector(`[data-syf-panel="${mode}"] [data-syf-option]`);
      requestAnimationFrame(() => target?.focus({ preventScroll: true }));
    }

    function showResult(mode, key) {
      const template = Array.from(root.querySelectorAll('[data-syf-template]')).find((item) => item.dataset.mode === mode && item.dataset.key === key);
      if (!template) return;
      resultContent.replaceChildren(template.content.cloneNode(true));
      discovery.hidden = true;
      result.hidden = false;
      root.dataset.syfState = key === 'yard-guide' ? 'yard-guide' : (key === 'not-sure' ? 'grass-identify' : 'result');
      root.dataset.syfMode = mode;
      root.dataset.syfKey = key;
      setToolbar(mode);
      focusHeading();
    }

    function activeIdentifierStep(identifier) {
      return identifier.querySelector('.syf-vyf__identifier-step.is-active');
    }

    function goIdentifierStep(identifier, nextNumber) {
      identifier.querySelectorAll('[data-syf-id-step]').forEach((step) => {
        const active = Number(step.dataset.syfIdStep) === nextNumber;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      const active = activeIdentifierStep(identifier);
      requestAnimationFrame(() => active?.querySelector('select,button:not([disabled])')?.focus());
    }

    function matchedGrass(identifier) {
      const state = identifier.querySelector('[data-syf-state]')?.value;
      const blade = identifier.querySelector('[data-syf-trait="blade"].is-selected')?.dataset.value;
      const growth = identifier.querySelector('[data-syf-trait="growth"].is-selected')?.dataset.value;
      if (blade === 'coarse' && growth === 'upright') return 'tall-fescue';
      if (blade === 'coarse' && growth === 'spreading') return 'st-augustine';
      if (blade === 'fine' && growth === 'dense') return 'kentucky-bluegrass';
      if ((blade === 'fine' || blade === 'medium') && growth === 'spreading') return 'bermuda';
      return stateMap[state] || 'tall-fescue';
    }

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-syf-state]');
      if (!select) return;
      const step = select.closest('[data-syf-id-step]');
      const next = step?.querySelector('[data-syf-id-next]');
      if (next) next.disabled = !select.value;
    }, { signal });

    root.addEventListener('keydown', (event) => {
      const option = event.target.closest('[data-syf-option],[data-syf-guide-option]');
      if (!option || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      showResult(option.dataset.mode, option.dataset.key);
    }, { signal });

    root.addEventListener('click', (event) => {
      const option = event.target.closest('[data-syf-option],[data-syf-guide-option]');
      if (option) {
        showResult(option.dataset.mode, option.dataset.key);
        return;
      }

      const view = event.target.closest('[data-syf-view]');
      if (view) {
        const shouldScrollToRoot = Boolean(view.closest('.syf-vyf__actions'));
        showDiscovery(view.dataset.syfView);
        if (shouldScrollToRoot) scrollToRoot();
        return;
      }

      const identifier = event.target.closest('[data-syf-identifier]');
      if (!identifier) return;

      const trait = event.target.closest('[data-syf-trait]');
      if (trait) {
        identifier.querySelectorAll(`[data-syf-trait="${trait.dataset.syfTrait}"]`).forEach((button) => button.classList.toggle('is-selected', button === trait));
        trait.setAttribute('aria-pressed', 'true');
        trait.parentElement.querySelectorAll('[data-syf-trait]').forEach((button) => { if (button !== trait) button.setAttribute('aria-pressed', 'false'); });
        const step = trait.closest('[data-syf-id-step]');
        step?.querySelector('[data-syf-id-next],[data-syf-identify]')?.removeAttribute('disabled');
        return;
      }

      if (event.target.closest('[data-syf-id-next]')) {
        const step = activeIdentifierStep(identifier);
        goIdentifierStep(identifier, Number(step?.dataset.syfIdStep || 1) + 1);
        return;
      }

      if (event.target.closest('[data-syf-id-back]')) {
        const step = activeIdentifierStep(identifier);
        goIdentifierStep(identifier, Math.max(1, Number(step?.dataset.syfIdStep || 2) - 1));
        return;
      }

      if (event.target.closest('[data-syf-identify]')) {
        const key = matchedGrass(identifier);
        const answer = identifier.querySelector('[data-syf-id-answer]');
        identifier.querySelectorAll('[data-syf-id-step]').forEach((step) => { step.hidden = true; step.classList.remove('is-active'); });
        answer.querySelector('[data-syf-id-name]').textContent = grassNames[key];
        answer.querySelector('[data-syf-id-detail]').textContent = grassDetails[key];
        answer.querySelector('[data-syf-open-match]').dataset.matchKey = key;
        answer.hidden = false;
        answer.querySelector('[data-syf-id-name]').setAttribute('tabindex', '-1');
        answer.querySelector('[data-syf-id-name]').focus();
        return;
      }

      const openMatch = event.target.closest('[data-syf-open-match]');
      if (openMatch) showResult('lawn', openMatch.dataset.matchKey);
    }, { signal });

    root.querySelectorAll('[data-syf-trait]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.('[data-syf-root]').forEach(init);
    if (scope.matches?.('[data-syf-root]')) init(scope);
  }

  if (!window[NS]) {
    window[NS] = { initAll };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
    else initAll();
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.querySelector?.('[data-syf-root]') || (event.target.matches?.('[data-syf-root]') ? event.target : null);
      root?.__syfController?.abort();
      if (root) delete root.__syfController;
    });
  } else {
    window[NS].initAll();
  }
})();
